import React, { useEffect, useMemo, useRef, useState } from 'react';
import { graphql, Link, useStaticQuery } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import CategoryMenuList, { useCategoryMenuItems } from "./CategoryMenuList";

const MEGA_MENU_DESIGN_HEIGHT = 592;
const MEGA_MENU_MIN_HEIGHT = 360;
const MEGA_MENU_BOTTOM_PADDING = 24;
const DEFAULT_AD_HREF = "#";

const isExternalUrl = (value) => /^https?:\/\//i.test(String(value || ""));
const isInternalPath = (value) => /^\/(?!\/)/.test(String(value || ""));

const MegaMenu = ({ isOpen }) => {
  const data = useStaticQuery(graphql`
    query MegaMenuBannersQuery {
      banners: wpPage(slug: {eq: "mega-menu"}) {
        megaMenu {
            banners {
              image {
                node {
                  altText
                  gatsbyImage(
                    width: 300
                    quality: 90
                    placeholder: BLURRED
                    layout: CONSTRAINED
                  )
                }
              }
              url
            }
          }
        }
      }
  `);
  const categoryItems = useCategoryMenuItems();
  const [activeCategory, setActiveCategory] = useState(null);
  const [visibleCategoryCount, setVisibleCategoryCount] = useState(0);
  const [menuMaxHeight, setMenuMaxHeight] = useState(MEGA_MENU_DESIGN_HEIGHT);
  const megaMenuRef = useRef(null);
  const leftPanelRef = useRef(null);
  const viewAllRef = useRef(null);
  const measureRef = useRef(null);

  useEffect(() => {
    if (!categoryItems.length) {
      setActiveCategory(null);
      setVisibleCategoryCount(0);
      return;
    }

    setVisibleCategoryCount((previousCount) => {
      if (!previousCount) {
        return categoryItems.length;
      }

      return Math.min(previousCount, categoryItems.length);
    });

    setActiveCategory((previousCategory) => {
      if (!previousCategory) {
        return categoryItems[0];
      }

      return categoryItems.find((category) => category.slug === previousCategory.slug) || categoryItems[0];
    });
  }, [categoryItems]);

  const visibleCategories = useMemo(
    () => categoryItems.slice(0, Math.max(1, visibleCategoryCount)),
    [categoryItems, visibleCategoryCount]
  );

  const selectedCategory = useMemo(() => {
    if (!activeCategory) {
      return visibleCategories[0] || categoryItems[0];
    }
    const activeIsVisible = visibleCategories.some((category) => category.name === activeCategory.name);
    return activeIsVisible ? activeCategory : visibleCategories[0] || categoryItems[0];
  }, [activeCategory, categoryItems, visibleCategories]);

  useEffect(() => {
    const updateVisibleCount = () => {
      if (!leftPanelRef.current || !viewAllRef.current || !measureRef.current) {
        return;
      }

      const listItems = Array.from(
        measureRef.current.querySelectorAll('[data-mega-category-item="true"]')
      );
      if (!listItems.length) {
        setVisibleCategoryCount(categoryItems.length);
        return;
      }

      const panelHeight = leftPanelRef.current.clientHeight;
      const effectivePanelHeight = Math.min(panelHeight, menuMaxHeight);
      const viewAllHeight = viewAllRef.current.offsetHeight;
      const listVerticalPadding = 24;
      const availableHeight = effectivePanelHeight - viewAllHeight - listVerticalPadding;

      let consumedHeight = 0;
      let fitCount = 0;
      for (let index = 0; index < listItems.length; index += 1) {
        const item = listItems[index];
        const nextHeight = consumedHeight + item.getBoundingClientRect().height;
        if (nextHeight <= availableHeight) {
          fitCount += 1;
          consumedHeight = nextHeight;
        } else {
          break;
        }
      }

      setVisibleCategoryCount(Math.max(1, fitCount));
    };

    updateVisibleCount();
    const resizeObserver = new ResizeObserver(updateVisibleCount);
    if (leftPanelRef.current) {
      resizeObserver.observe(leftPanelRef.current);
    }
    if (viewAllRef.current) {
      resizeObserver.observe(viewAllRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [categoryItems.length, menuMaxHeight]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const updateMenuMaxHeight = () => {
      if (!megaMenuRef.current) {
        return;
      }

      const menuTop = megaMenuRef.current.getBoundingClientRect().top;
      const availableHeight = window.innerHeight - menuTop - MEGA_MENU_BOTTOM_PADDING;
      const nextMaxHeight = Math.max(
        MEGA_MENU_MIN_HEIGHT,
        Math.min(MEGA_MENU_DESIGN_HEIGHT, Math.floor(availableHeight))
      );

      setMenuMaxHeight(nextMaxHeight);
    };

    const rafId = window.requestAnimationFrame(updateMenuMaxHeight);
    window.addEventListener('resize', updateMenuMaxHeight);

    return () => {
      window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', updateMenuMaxHeight);
    };
  }, [isOpen]);

  const normalizedSubcategories = useMemo(
    () =>
      (selectedCategory?.subcategories || selectedCategory?.children || [])
        .map((subcategory) => {
          if (typeof subcategory === "string") {
            return { name: subcategory, slug: "", children: [] };
          }

          const rawChildren = Array.isArray(subcategory?.subcategories)
            ? subcategory.subcategories
            : Array.isArray(subcategory?.children)
              ? subcategory.children
              : [];

          return {
            name: subcategory?.name || "",
            slug: subcategory?.slug || "",
            children: rawChildren.map((childNode) =>
              typeof childNode === "string"
                ? { name: childNode, slug: "" }
                : { name: childNode?.name || "", slug: childNode?.slug || "" }
            ),
          };
        })
        .filter((subcategory) => Boolean(subcategory.name)),
    [selectedCategory]
  );
  const megaMenuAds = useMemo(
    () =>
      (data?.banners?.megaMenu?.banners || [])
        .map((banner, index) => {
          const imageNode = banner?.image?.node;
          const imageData = getImage(imageNode?.gatsbyImage);
          const resolvedHref = banner?.url?.trim() || DEFAULT_AD_HREF;

          if (!imageData) {
            return null;
          }

          return {
            id: `mega-menu-ad-${index}`,
            imageData,
            altText: imageNode?.altText?.trim() || `Mega menu ad ${index + 1}`,
            href: resolvedHref,
          };
        })
        .filter(Boolean),
    [data]
  );
  const viewAllCategoryPath = "/shop-brand";

  return (
    <>
      <div
        aria-hidden="true"
        className={`fixed left-0 right-0 bg-[#6B6B6B] transition-opacity duration-300 ease-out ${
          isOpen ? 'opacity-30' : 'opacity-0'
        } pointer-events-none z-30`}
        style={{
          top: 'var(--header-height, 0px)',
          height: 'calc(100vh - var(--header-height, 0px))',
        }}
      />

      <div
        ref={megaMenuRef}
        className={`absolute top-full left-0 right-0 w-full bg-white rounded-b-[8px] shadow-shadow-3 z-40 mt-0 transform transition-all duration-300 ease-out ${
          isOpen ? 'translate-y-0 opacity-100 pointer-events-auto overflow-y-auto' : '-translate-y-3 opacity-0 pointer-events-none'
        }`}
        style={{ maxHeight: `${menuMaxHeight}px` }}
      >
        <div className="grid h-[592px] bg-white rounded-b-[8px] overflow-hidden grid-cols-[236px_minmax(0,1fr)] desktop-xl:grid-cols-[265px_minmax(0,1fr)]">
        {/* Left Panel - Category Menu */}
        <div ref={leftPanelRef} className="relative h-full bg-white border-r border-mqgrey1">
          <div className="flex h-full flex-col overflow-hidden px-[16px] py-[12px]">
            <CategoryMenuList
              categories={visibleCategories}
              variant="mega"
              activeCategory={selectedCategory}
              onCategoryChange={setActiveCategory}
              itemClassName="w-[204px] desktop-xl:w-[233px]"
            />

            <div ref={viewAllRef} className="pt-[8px]">
              <Link
                to={viewAllCategoryPath}
                className="inline-flex items-center justify-center w-[204px] desktop-xl:w-[233px] bg-[#575757] text-white text-[14px] leading-[24px] font-work-sans font-medium py-[8px] px-[16px] rounded-[8px] hover:bg-[#4c4c4c] transition-colors hover:no-underline"
              >
                View All Products
              </Link>
            </div>
          </div>

          <div className="absolute inset-x-0 top-0 invisible pointer-events-none" aria-hidden="true">
            <div ref={measureRef} className="px-[16px] py-[12px]">
              <CategoryMenuList
                categories={categoryItems}
                variant="mega"
                activeCategory={selectedCategory}
                itemClassName="w-[204px] desktop-xl:w-[233px]"
                className=""
              />
            </div>
          </div>
        </div>

        {/* Right Panel - Product Content */}
        <div className="bg-[#FCFCFC] p-[24px] overflow-hidden">
          <div className="grid h-full gap-[20px] grid-cols-[minmax(0,1fr)_220px] desktop-xl:grid-cols-[minmax(0,1fr)_246px]">
            <div className="h-full overflow-y-auto pr-[8px] [column-gap:20px] [column-count:2] [column-fill:auto] [column-width:140px] desktop-xl:[column-width:165px]">
              {normalizedSubcategories.map((subcategory) => (
                <div key={subcategory.name} className="mb-[14px] break-inside-avoid">
                  {subcategory.slug ? (
                    <Link
                      to={`/shop-category/${subcategory.slug}`}
                      className="text-left text-[14px] leading-[24px] font-work-sans font-medium text-black hover:text-mqred7 transition-colors cursor-pointer select-none"
                    >
                      {subcategory.name}
                    </Link>
                  ) : (
                    <div className="text-left text-[14px] leading-[24px] font-work-sans font-medium text-black">
                      {subcategory.name}
                    </div>
                  )}
                  {subcategory.children.map((childCategory, index) => (
                    childCategory?.slug ? (
                      <Link
                        key={`${subcategory.name}-${typeof childCategory === 'string' ? childCategory : childCategory?.name || `child-${index}`}`}
                        to={`/shop-category/${childCategory.slug}`}
                        className="block pl-[16px] mt-[2px] text-[14px] leading-[24px] font-work-sans font-normal text-[#858585] hover:text-mqred7 transition-colors cursor-pointer select-none"
                      >
                        {typeof childCategory === 'string' ? childCategory : childCategory?.name}
                      </Link>
                    ) : (
                      <div
                        key={`${subcategory.name}-${typeof childCategory === 'string' ? childCategory : childCategory?.name || `child-${index}`}`}
                        className="block pl-[16px] mt-[2px] text-[14px] leading-[24px] font-work-sans font-normal text-[#858585]"
                      >
                        {typeof childCategory === 'string' ? childCategory : childCategory?.name}
                      </div>
                    )
                  ))}
                </div>
              ))}
            </div>

            <div className="h-full overflow-y-auto pr-[6px]">
              <div className="flex min-h-full flex-col gap-[16px]">
                {megaMenuAds.map((ad) => {
                  const adFrame = (
                    <div className="h-[247px] desktop-xl:h-[257px] rounded-[4px] border border-[#EE3214] overflow-hidden bg-white">
                      <GatsbyImage
                        image={ad.imageData}
                        alt={ad.altText}
                        className="h-full w-full"
                        imgClassName="h-full w-full object-contain"
                      />
                    </div>
                  );

                  if (ad.href !== DEFAULT_AD_HREF && isInternalPath(ad.href)) {
                    return (
                      <Link key={ad.id} to={ad.href} className="block hover:no-underline">
                        {adFrame}
                      </Link>
                    );
                  }

                  return (
                    <a
                      key={ad.id}
                      href={ad.href}
                      className="block"
                      rel={isExternalUrl(ad.href) ? "noopener noreferrer" : undefined}
                    >
                      {adFrame}
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

export default MegaMenu;
